import type {
  OpsRisksResponse,
  SetOpsRiskStateRequest,
  SetOpsRiskStateResponse,
} from '@nadoutong/contracts';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

/**
 * Typed wrapper + React Query hooks for `/v1/ops/risks` — the slim
 * unified risk feed (Epic #853 / #857 / #858) plus the workbench
 * ack/resolve state mutation (epic #997 / #1005).
 *
 * This is the single client-side source for "how many risks are open":
 * the shell RiskChip consumes `summary.open`, which counts supply,
 * finance, AND traffic dimensions with one severity vocabulary
 * (`critical | warning | info`), and since #1736 counts only items no
 * operator has acked or resolved (`summary.total` carries the unfiltered
 * population). It replaces the legacy
 * `/v1/ops/health/dashboard-alerts` four-array summing, which missed
 * finance and traffic entirely. The workbench additionally reads the
 * additive `groups` view and per-item `state` from the same payload.
 *
 * Query key is flat because the endpoint takes no parameters — window
 * semantics per dimension are fixed server-side and carried back in
 * `windows` (supply = 24h snapshot, finance = unresolved backlog,
 * traffic = the traffic overview's own current window).
 */

const BASE_PATH = '/v1/ops/risks';

export const OPS_RISKS_QUERY_KEY = ['ops-risks'] as const;

export async function getOpsRisks(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<OpsRisksResponse> {
  return opsApiRequest<OpsRisksResponse>(BASE_PATH, { signal }, fetchImpl);
}

export function useOpsRisks(): UseQueryResult<OpsRisksResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: OPS_RISKS_QUERY_KEY,
    queryFn: ({ signal }) => getOpsRisks(fetchImpl, signal),
  });
}

export async function setOpsRiskState(
  body: SetOpsRiskStateRequest,
  fetchImpl: typeof fetch,
): Promise<SetOpsRiskStateResponse> {
  return opsApiRequest<SetOpsRiskStateResponse>(
    `${BASE_PATH}/state`,
    { method: 'POST', body },
    fetchImpl,
  );
}

/**
 * Ack / resolve / reopen one risk fingerprint (#1005). The actor is
 * session-derived server-side (ops-api stamps `X-Ops-User-Id`); the
 * client only sends fingerprint/status/note. Invalidates the flat
 * risks query so the workbench AND the shell RiskChip refetch the
 * composed state together.
 */
export function useSetOpsRiskState(): UseMutationResult<
  SetOpsRiskStateResponse,
  Error,
  SetOpsRiskStateRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => setOpsRiskState(body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: OPS_RISKS_QUERY_KEY });
    },
  });
}
